import React, { useState, useRef, useEffect } from 'react';
import '../../../styles/resume-editor.css';
import { LayoutSelector } from './LayoutSelector';
import { AddCustomSectionEntry } from './AddCustomSectionEntry';
import { CustomSectionEntryCard } from './CustomSectionEntryCard';

export interface CustomSectionEntry {
  title: string;
  description?: string;
  date?: Date | null;
  endDate?: Date | null;
  yearOnly?: boolean;
  hideDate?: boolean;
  hideEndDate?: boolean;
}

interface CustomSectionExpandedProps {
  title: string;
  layout: 'inline' | 'stacked';
  entries: Array<CustomSectionEntry & { id: string }>;
  onCollapse: () => void;
  onAddEntry: (entry: CustomSectionEntry) => void;
  onEditEntry: (entry: CustomSectionEntry & { id: string }) => void;
  onDeleteEntry: (id: string) => void;
  onLayoutChange: (layout: 'inline' | 'stacked') => void;
  onDelete: () => void;
}

export const CustomSectionExpanded: React.FC<CustomSectionExpandedProps> = ({
  title,
  layout,
  entries,
  onCollapse,
  onAddEntry,
  onEditEntry,
  onDeleteEntry,
  onLayoutChange,
  onDelete,
}) => {
  const [showLayoutSelection, setShowLayoutSelection] = useState(false);
  const [showAddEntryForm, setShowAddEntryForm] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  if (showLayoutSelection) {
    return (
      <div className="resume-editor__section-expanded">
        <LayoutSelector
          currentLayout={layout}
          onBack={() => setShowLayoutSelection(false)}
          onSelect={(newLayout) => {
            onLayoutChange(newLayout);
            setShowLayoutSelection(false);
          }}
        />
      </div>
    );
  }

  if (showAddEntryForm || editingEntryId) {
    const editingEntry = editingEntryId ? entries.find(e => e.id === editingEntryId) : null;
    return (
      <div className="resume-editor__section-expanded">
        <AddCustomSectionEntry
          sectionTitle={title}
          initialEntry={editingEntry ? {
            title: editingEntry.title,
            description: editingEntry.description,
            date: editingEntry.date,
            endDate: editingEntry.endDate,
            yearOnly: editingEntry.yearOnly,
            hideDate: editingEntry.hideDate,
            hideEndDate: editingEntry.hideEndDate,
          } : undefined}
          onSave={(entry) => {
            if (editingEntryId) {
              onEditEntry({ ...entry, id: editingEntryId });
              setEditingEntryId(null);
            } else {
              onAddEntry(entry);
              setShowAddEntryForm(false);
            }
          }}
          onCancel={() => {
            setShowAddEntryForm(false);
            setEditingEntryId(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="resume-editor__section-expanded">
      <div className="resume-editor__section-expanded-header">
        <button
          className="resume-editor__section-toggle resume-editor__section-toggle--expanded"
          onClick={onCollapse}
          title="Collapse section"
        >
          −
        </button>
        <span className="resume-editor__section-text">{title}</span>
        <div className="resume-editor__experience-card-menu-container" ref={menuRef}>
          <button
            className="resume-editor__section-menu-btn"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            title="More options"
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="resume-editor__experience-card-dropdown">
              <button
                className="resume-editor__experience-card-dropdown-item resume-editor__experience-card-dropdown-item--delete"
                onClick={() => {
                  onDelete();
                  setMenuOpen(false);
                }}
              >
                Delete section
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="resume-editor__section-expanded-body">
        <div style={{ marginBottom: entries.length > 0 ? '12px' : '0', display: 'flex', gap: '8px' }}>
          <button
            className="custom-section-expanded__action-btn"
            onClick={() => setShowAddEntryForm(true)}
            style={{ width: '75%' }}
          >
            <svg
              className="custom-section-expanded__action-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 3V13M3 8H13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span>Add Entry</span>
          </button>
          <button
            className="custom-section-expanded__action-btn"
            onClick={() => setShowLayoutSelection(true)}
            style={{ width: '25%' }}
          >
            <svg
              className="custom-section-expanded__action-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 4H14M2 8H14M2 12H14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span>Layout</span>
          </button>
        </div>

        {entries.length > 0 && (
          <>
            {entries.map((entry) => (
              <CustomSectionEntryCard
                key={entry.id}
                entry={entry}
                layout={layout}
                onEdit={(entry) => setEditingEntryId(entry.id)}
                onDelete={onDeleteEntry}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

